import { fmtDate } from "@/lib/dateFormat";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Banknote, Hammer, CheckCircle2, Car, Plus, Search, UserPlus, FileText, Receipt, ArrowRight, AlertCircle, Bell, SkipForward } from "lucide-react";
import { api, money } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/components/PageHeader";
import { UniversalSearch } from "@/components/UniversalSearch";
import { ServiceRemindersCard } from "@/components/ServiceRemindersCard";
import { Loading, ErrorState, EmptyState } from "@/components/States";
import { StatusChip } from "@/components/StatusChip";
import { NumberPlate } from "@/components/NumberPlate";

const QUICK = [
  { label: "New Job", icon: Hammer, to: "/jobs/new", testId: "quick-new-job" },
  { label: "Create Invoice", icon: Receipt, to: "/invoice/new", testId: "quick-create-invoice" },
  { label: "DVLA / MOT Lookup", icon: Search, to: "/mot-lookup", testId: "quick-mot-lookup" },
  { label: "New Customer", icon: UserPlus, to: "/customers/new", testId: "quick-new-customer" },
  { label: "New Quotation", icon: FileText, to: "/quotation-new", testId: "quick-new-quotation" },
];

function StatCard({ icon: Icon, label, value, tone = "brand", onClick, testid }) {
  const tones = { brand: "text-brand bg-brand-tertiary", success: "text-success bg-success/10", midnight: "text-midnight bg-secondary", danger: "text-danger bg-danger-bg" };
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className="rounded-xl border border-border bg-card p-5 text-left transition-all hover:border-brand hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-brand/30">
      <div className={`mb-3 flex h-10 w-10 items-center justify-center rounded-lg ${tones[tone]}`}><Icon className="h-5 w-5" /></div>
      <p className="font-display text-3xl font-bold text-midnight">{value}</p>
      <p className="mt-0.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
    </button>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);

  const loadNotifs = async () => {
    try { const r = await api("/notifications"); setNotifs(r.items || []); setUnread(r.unread_count || 0); }
    catch { /* ignore */ }
  };
  useEffect(() => { loadNotifs(); }, []);

  const openNotif = async (n) => {
    try { await api("/notifications/read", { method: "POST", body: JSON.stringify({ keys: [n.key] }) }); } catch { /* ignore */ }
    if (n.link) navigate(n.link);
  };
  const markAllRead = async () => {
    try { await api("/notifications/read-all", { method: "POST" }); } catch { /* ignore */ }
    loadNotifs();
  };

  const load = async () => {
    setLoading(true); setError(null);
    try { setData(await api("/reports/dashboard")); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div>
      <PageHeader
        title={`Welcome, ${(user?.name || "").split(" ")[0] || "there"}`}
        subtitle="Your workshop at a glance"
        actions={<Link to="/jobs/new" data-testid="dashboard-new-job" className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2.5 text-sm font-bold text-white transition-transform hover:scale-105"><Plus className="h-4 w-4" />New Job</Link>}
      />

      <UniversalSearch />

      <ServiceRemindersCard />

      {loading ? <Loading /> : error ? <ErrorState message={error} onRetry={load} /> : (
        <>
          {data.skipped_followups > 0 && (
            <Link to="/jobs?status=skipped" data-testid="dashboard-skipped-banner"
              className="mb-6 flex items-center gap-3 rounded-xl border border-amber-400/50 bg-amber-50 p-4 transition-colors hover:bg-amber-100">
              <SkipForward className="h-5 w-5 text-amber-600" />
              <p className="flex-1 text-sm font-bold text-midnight">
                {data.skipped_followups} {data.skipped_followups === 1 ? "job has" : "jobs have"} skipped steps
                <span className="ml-1 font-medium text-muted-foreground">— review and follow up</span>
              </p>
              <span data-testid="dashboard-skipped-badge" className="inline-flex h-6 min-w-[24px] items-center justify-center rounded-full bg-amber-500 px-2 text-xs font-extrabold text-white">{data.skipped_followups > 99 ? "99+" : data.skipped_followups}</span>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
            </Link>
          )}
          {notifs.length > 0 && (
            <div className="mb-6 rounded-xl border border-border bg-card p-4" data-testid="dashboard-notifications">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="flex items-center gap-2 font-display text-lg font-bold uppercase text-midnight">
                  <Bell className="h-5 w-5 text-brand" />Notifications
                  {unread > 0 && <span data-testid="notif-unread-count" className="rounded-full bg-brand px-2 py-0.5 text-[11px] font-extrabold text-white">{unread}</span>}
                </h2>
                {unread > 0 && <button onClick={markAllRead} data-testid="notif-mark-all-read" className="text-xs font-bold text-brand-2 hover:underline">Mark all read</button>}
              </div>
              <div className="divide-y divide-border">
                {notifs.slice(0, 8).map((n) => (
                  <button key={n.key} onClick={() => openNotif(n)} data-testid={`notif-${n.key}`}
                    className={`flex w-full items-center gap-3 py-2.5 text-left transition-colors hover:bg-secondary ${n.read ? "opacity-60" : ""}`}>
                    {!n.read && <span className="h-2 w-2 shrink-0 rounded-full bg-brand" data-testid={`notif-dot-${n.key}`} />}
                    {n.read && <span className="h-2 w-2 shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-bold text-midnight">{n.title}</p>
                      {n.body && <p className="truncate text-xs text-muted-foreground">{n.body}</p>}
                    </div>
                    <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
            <StatCard icon={Banknote} label="Revenue (7 days)" value={money(data.revenue_week)} tone="success" onClick={() => navigate("/money")} testid="stat-revenue" />
            <StatCard icon={AlertCircle} label={data.overdue_count ? `Outstanding · ${data.overdue_count} overdue` : "Outstanding"} value={money(data.total_outstanding || 0)} tone="danger" onClick={() => navigate("/money")} testid="stat-outstanding" />
            <StatCard icon={Hammer} label={data.my_active_jobs ? `Active jobs · ${data.my_active_jobs} yours` : "Active jobs"} value={data.active_jobs} tone="brand" onClick={() => navigate("/jobs?status=active")} testid="stat-active-jobs" />
            <StatCard icon={CheckCircle2} label="Completed today" value={data.completed_today} tone="success" onClick={() => navigate("/jobs?status=completed_today")} testid="stat-completed-today" />
            <StatCard icon={Car} label="Vehicles" value={data.vehicles_count} tone="midnight" onClick={() => navigate("/vehicles")} testid="stat-vehicles" />
          </div>

          <div className="mt-6 grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
            {QUICK.map((q) => {
              const Icon = q.icon;
              return (
                <Link key={q.to} to={q.to} data-testid={q.testId}
                  className="group flex items-center gap-3 rounded-xl border border-border bg-card p-4 transition-colors hover:border-brand hover:bg-brand-tertiary">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-tertiary text-brand group-hover:bg-white"><Icon className="h-5 w-5" /></div>
                  <span className="text-sm font-bold text-midnight">{q.label}</span>
                </Link>
              );
            })}
          </div>

          <div className="mt-8">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-display text-xl font-bold uppercase text-midnight">Active Jobs</h2>
              <Link to="/jobs" className="inline-flex items-center gap-1 text-sm font-semibold text-brand-2">View all <ArrowRight className="h-4 w-4" /></Link>
            </div>
            {(!data.today_jobs || data.today_jobs.length === 0) ? (
              <EmptyState icon={Hammer} title="No active jobs" description="Create a job to start tracking work in your workshop." action={<Link to="/jobs/new" className="rounded-lg bg-brand px-4 py-2.5 text-sm font-bold text-white">New Job</Link>} />
            ) : (
              <div className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-card">
                {data.today_jobs.map((j) => {
                  const isMine = j.assigned_to && j.assigned_to === user?.id;
                  return (
                  <button key={j.id} onClick={() => navigate(`/jobs/${j.id}`)} data-testid={`dashboard-job-${j.id}`}
                    className={`flex w-full items-center gap-4 px-4 py-3.5 text-left transition-colors hover:bg-secondary ${isMine ? "border-l-4 border-brand bg-brand-tertiary/40" : ""}`}>
                    {j.vehicle?.vrm ? <NumberPlate vrm={j.vehicle.vrm} size="sm" /> : <div className="h-7 w-24 rounded bg-secondary" />}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-bold text-midnight">{j.vehicle ? `${j.vehicle.make || ""} ${j.vehicle.model || ""}` : "Vehicle"}</p>
                      <p className="truncate text-xs text-muted-foreground">{j.title || j.category || "Job"}{j.assignee ? ` · ${isMine ? "You" : (j.assignee.name || j.assignee.email)}` : ""}</p>
                    </div>
                    {isMine && <span className="rounded-full bg-brand px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-white" data-testid={`dashboard-mine-${j.id}`}>You</span>}
                    <StatusChip status={j.status} />
                  </button>
                );})}
              </div>
            )}
          </div>

          {data.upcoming_bookings && data.upcoming_bookings.length > 0 && (
            <div className="mt-8">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="font-display text-xl font-bold uppercase text-midnight">Upcoming Bookings</h2>
                <Link to="/calendar" className="inline-flex items-center gap-1 text-sm font-semibold text-brand-2">View calendar <ArrowRight className="h-4 w-4" /></Link>
              </div>
              <div className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-card">
                {data.upcoming_bookings.map((b) => (
                  <button key={b.id} onClick={() => navigate("/calendar")} data-testid={`dashboard-booking-${b.id}`}
                    className="flex w-full items-center gap-4 px-4 py-3.5 text-left transition-colors hover:bg-secondary">
                    <div className="w-24 shrink-0">
                      <p className="text-sm font-bold text-midnight">{fmtDate(b.start_local || b.start_at)}</p>
                      <p className="text-xs text-muted-foreground">{(b.start_local || b.start_at || "").slice(11, 16)} · {b.duration_minutes}m</p>
                    </div>
                    {b.vehicle?.vrm ? <NumberPlate vrm={b.vehicle.vrm} size="sm" /> : <div className="h-7 w-24 rounded bg-secondary" />}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-bold text-midnight">{b.reason}</p>
                      <p className="truncate text-xs text-muted-foreground">{b.vehicle ? `${b.vehicle.make || ""} ${b.vehicle.model || ""}` : ""}{b.assignee ? ` · ${b.assignee.name}` : ""}</p>
                    </div>
                    <StatusChip status={b.display_status || "booked"} />
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
